import { useEffect, useMemo, useState } from 'react';
import { Alert, Group, LoadingOverlay, Pagination, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

import { useVendorMenuTemplate } from '../hooks/useVendorMenuTemplate';
import { groupItemsByCategory } from '../helpers';
import {
  MENU_LOAD_ERROR_TITLE,
  NO_MENU_ITEMS_TEXT,
  UNCATEGORIZED_CATEGORY_LABEL,
} from '../constants';
import { MenuEmptyState } from '../components/MenuEmptyState';

import { MenuDetailsHeader } from './MenuDetails.header';
import { MenuItemRow } from './MenuItemRow';

type MenuDetailsSectionProps = {
  templateId: string;
};

export const MenuDetailsSection: React.FC<MenuDetailsSectionProps> = ({ templateId }) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [templateId]);

  const { template, items, totalCount, pageCount, loading, error } = useVendorMenuTemplate(
    templateId,
    page
  );

  const sections = useMemo(
    () => (template ? groupItemsByCategory(items, template.categories) : []),
    [items, template]
  );

  if (error) {
    return (
      <Alert
        m="lg"
        variant="light"
        color="red"
        icon={<IconAlertCircle size={18} />}
        title={MENU_LOAD_ERROR_TITLE}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Stack gap="lg" p="lg" pos="relative" mih={400}>
      <LoadingOverlay visible={loading} />

      {template && <MenuDetailsHeader template={template} />}

      {!loading && !totalCount ? (
        <MenuEmptyState title={NO_MENU_ITEMS_TEXT} />
      ) : (
        <Stack gap="xl">
          {sections.map((section, index) => (
            <Stack key={section.category?.id ?? `uncategorized-${index}`} gap={0}>
              <Text fw={700} size="md" mb={4}>
                {section.category?.name ?? UNCATEGORIZED_CATEGORY_LABEL}
              </Text>
              <Stack gap="xs">
                {section.items.map((item) => (
                  <MenuItemRow key={item.id} item={item} />
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {pageCount > 1 && (
        <Group justify="center" pt="sm">
          <Pagination value={page} onChange={setPage} total={pageCount} size="sm" withEdges />
        </Group>
      )}
    </Stack>
  );
};
